import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Grid, Link } from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import { Button, Checkbox } from '@totango-app-team/ui-shared-components';
import classNames from 'classnames';
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';

import Input from 'Components/input/input';
import RadioGroup from 'Components/radio-group/radio-group';
import Select from 'Components/select/select';
import useMobileDetect from 'Hooks/useMobileDetect';
import usePrevious from 'Hooks/usePrevious';

import { validateSubDomain } from '../utils/api';
import {
	accountsOptions,
	companyDescriptionsOptions,
	dataCenterOptions,
	employeeNumberOptions,
	PRIVACY_POLICY_URL,
	TERMS_AND_CONDITIONS_URL,
} from '../utils/constants';

const checkboxLabelClasses = {
	root: 'agree-checkbox-wrapper',
};

export default function AccountSettings(props) {
	const {
		username,
		generateAuthenticationCode,
		isSignInWithOauth,
		handleNext,
		title,
		accountName,
		setAccountName,
		shouldShowDataCenterOptions,
		dataCenter,
		setDataCenter,
		employeeNumber,
		setEmployeeNumber,
		companyDescription,
		setCompanyDescription,
		numberOfAccounts,
		setNumberOfAccounts,
		setIsLoading,
		isLoading,
		isActiveStep,
		setForceUpdateHeight,
		agreedReceiveContent,
		setAgreedReceiveContent,
	} = props;
	const [accountDomainName, setAccountDomainName] = useState(accountName);
	const [isDisabled, setIsDisabled] = useState(true);
	const [validationError, setValidationError] = useState(false);
	const [validationErrorMessage, setValidationErrorMessage] = useState('');
	const { isMobile } = useMobileDetect();
	const prevAccountName = usePrevious(accountName);

	const onDataCenterChanged = useCallback(
		e => {
			setDataCenter(e.target.value);
		},
		[setDataCenter],
	);

	const validateName = useCallback(
		debounce(newName => {
			const validateUsername = async subdomain => {
				try {
					const { validation, suggestion } = await validateSubDomain(subdomain);

					if (validation) {
						setIsLoading(false);
						setValidationError(false);
						setValidationErrorMessage(undefined);
						setAccountDomainName(subdomain);
						setAccountName(subdomain);
					} else {
						setIsLoading(false);
						if (isActiveStep) {
							setValidationError(true);
							setValidationErrorMessage(
								`Account name already exists on Totango${suggestion ? `, try ${suggestion}` : ''}`,
							);
						} else if (suggestion) {
							setValidationErrorMessage(undefined);
							setAccountDomainName(suggestion);
							setAccountName(suggestion);
						}
					}
				} catch (err) {
					setIsLoading(false);
					setValidationError(true);
					setValidationErrorMessage('Error validating account name, please try again.');
				}
			};

			if (newName && (newName.length < 3 || newName.length > 20)) {
				setIsLoading(false);
				setValidationError(true);
				setValidationErrorMessage('Account name should be 3-20 characters long');
				return;
			}

			if (!/^[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]$/g.test(newName)) {
				setIsLoading(false);
				setValidationError(true);
				setValidationErrorMessage('Account name should be in a valid subdomain format');
				return;
			}

			setValidationError(false);
			setValidationErrorMessage('');
			setIsLoading(true);
			validateUsername(newName);
		}, 1000),
		[setIsLoading, isActiveStep],
	);

	const onEmployeeNumberChange = useCallback(
		e => {
			e.persist();
			setEmployeeNumber(e.target.value);
		},
		[setEmployeeNumber],
	);

	const onAccountNameChange = useCallback(
		e => {
			e.persist();
			setAccountDomainName(e.target.value);
			validateName(e.target.value);
		},
		[validateName],
	);

	const onCompanyDescriptionChange = useCallback(
		e => {
			e.persist();
			setCompanyDescription(e.target.value);
		},
		[setCompanyDescription],
	);

	const onNumberOfAccountsChange = useCallback(
		e => {
			e.persist();
			setNumberOfAccounts(e.target.value);
		},
		[setNumberOfAccounts],
	);

	const onAgreedReceiveContentChange = useCallback(
		e => {
			e.persist();
			setAgreedReceiveContent(e.target.checked);
		},
		[setAgreedReceiveContent],
	);

	const onButtonClick = useCallback(
		async e => {
			if (!validationError) {
				generateAuthenticationCode(username, !isSignInWithOauth);
				if (!isSignInWithOauth) {
					handleNext();
				}
			}
		},
		[validationError, handleNext, generateAuthenticationCode, isSignInWithOauth, username],
	);

	useEffect(() => {
		if (prevAccountName !== accountName && accountName !== accountDomainName) {
			validateName(accountName);
		}
	}, [prevAccountName, accountName, validateName, accountDomainName]);

	useEffect(() => {
		setIsDisabled(
			isEmpty(accountName) ||
				isEmpty(employeeNumber) ||
				isEmpty(companyDescription) ||
				isEmpty(numberOfAccounts) ||
				validationErrorMessage !== undefined,
		);
	}, [accountName, companyDescription, numberOfAccounts, employeeNumber, validationErrorMessage]);

	useEffect(() => {
		if (validationError !== undefined) {
			setForceUpdateHeight(true);
		}
	}, [setForceUpdateHeight, validationError]);

	const tabIndex = useMemo(() => (!isActiveStep ? 1 : undefined), [isActiveStep]);
	const accountNameInputLabel = useMemo(
		() => (isMobile() ? 'Account name' : 'Create your account name'),
		[isMobile],
	);

	const onKeyUpHandler = useCallback(
		e => {
			if (e && e.keyCode === 13 && isActiveStep && !isDisabled) {
				onButtonClick();
			}
		},
		[onButtonClick, isActiveStep, isDisabled],
	);

	return (
		<Grid container spacing={5} className="step-container account-settings">
			<Grid item xs={12}>
				<h5 className="step-title" align="center">
					{title}
				</h5>
				<div className="step-description">
					{'Help us make your experience better by telling us:'}
				</div>
			</Grid>
			<Grid item xs={2}>
				<span className="totango-domain-prefix">https://</span>
			</Grid>
			<Grid item xs={7} style={{ marginLeft: -15, marginRight: -15 }}>
				<Input
					id="account-name"
					name="accountName"
					variant="outlined"
					value={accountDomainName}
					label={accountNameInputLabel}
					fullWidth
					onChange={onAccountNameChange}
					error={validationError}
					autoComplete="no"
					InputProps={{
						inputProps: {
							tabIndex,
							maxLength: 20,
						},
					}}
				/>
			</Grid>
			<Grid item xs={3}>
				<span className="totango-domain">.totango.com</span>
			</Grid>
			{validationError && (
				<Grid
					item
					xs={12}
					className={classNames(
						'step-validation account-settings',
						validationError ? 'invalid' : '',
					)}
				>
					{validationError ? validationErrorMessage : ''}
				</Grid>
			)}
			{shouldShowDataCenterOptions && (
				<Grid item xs={12}>
					<Grid
						container
						className="select-data-center"
						alignItems="center"
						justifyContent="space-between"
					>
						<div className="data-center-label">Select data center location:</div>
						<RadioGroup
							className="data-center-options"
							row
							options={dataCenterOptions}
							onChange={onDataCenterChanged}
							value={dataCenter}
						/>
					</Grid>
				</Grid>
			)}
			<Grid item xs={12}>
				<Select
					fullWidth
					variant="outlined"
					value={companyDescription}
					onChange={onCompanyDescriptionChange}
					options={companyDescriptionsOptions}
					name="companyDescription"
					label="What best describes you and your company?"
				/>
			</Grid>
			<Grid item xs={12}>
				<Select
					fullWidth
					variant="outlined"
					value={employeeNumber}
					onChange={onEmployeeNumberChange}
					options={employeeNumberOptions}
					name="location"
					label="How many people work at your company?"
					inputProps={{ tabIndex }}
				/>
			</Grid>
			<Grid item xs={12}>
				<Select
					fullWidth
					variant="outlined"
					value={numberOfAccounts}
					onChange={onNumberOfAccountsChange}
					options={accountsOptions}
					name="numberOfAccounts"
					label="How many customers does your company have?"
					inputProps={{ tabIndex }}
				/>
			</Grid>
			<Grid item xs={12}>
				<Checkbox
					checked={agreedReceiveContent}
					labelClasses={checkboxLabelClasses}
					onChange={onAgreedReceiveContentChange}
					color="primary"
					label="I agree to receive relevant content from Totango"
					inputProps={{ tabIndex }}
				/>
			</Grid>
			<Grid item xs={12}>
				<Button
					color="primary"
					variant="contained"
					label="Continue"
					onClick={onButtonClick}
					onKeyUp={onKeyUpHandler}
					disabled={isDisabled}
					fullWidth
					{...tabIndex}
				>
					{isLoading ? <CircularProgress size={24} color="inherit" /> : 'Confirm'}
				</Button>
				<div className="terms-and-privacy-policy">
					{'By clicking on Confirm, you agree to our '}
					<Link href={TERMS_AND_CONDITIONS_URL} target="_blank" {...tabIndex}>
						Terms of Service
					</Link>
					<br />
					{'and you acknowledge having read our '}
					<Link href={PRIVACY_POLICY_URL} target="_blank" {...tabIndex}>
						Privacy Policy
					</Link>
				</div>
			</Grid>
		</Grid>
	);
}
